import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import MyModal from "./MyModal";
import useTranslation from "hooks/useTranslation";

export interface BulkOverrideConfirmProps {
  open: boolean;
  count: number;
  entityLabel: string;
  mixed: boolean;
  isLoading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function BulkOverrideConfirm({
  open,
  count,
  entityLabel,
  mixed,
  isLoading,
  onCancel,
  onConfirm
}: BulkOverrideConfirmProps) {
  const t = useTranslation();
  if (!open) return null;

  const message = mixed
    ? t["These items currently have different settings. Saving will override all existing settings with the values you set."] ||
      "These items currently have different settings. Saving will override all existing settings with the values you set."
    : t["Saving will apply these settings to all selected items and replace their current values."] ||
      "Saving will apply these settings to all selected items and replace their current values.";

  return (
    <MyModal
      title={t["Confirm Override"] || "Confirm Override"}
      subtitle={`${count} ${entityLabel}`}
      open={true}
      setOpen={onCancel}
      variant="light"
      width={440}
    >
      <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14, lineHeight: 1.5, mb: 1 }}>
        {message}
      </Typography>
      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          disabled={isLoading}
          sx={{
            minWidth: 120,
            height: 44,
            borderRadius: `${COLORS.radius.md}px`,
            borderColor: COLORS.surface.border,
            color: COLORS.surface.primaryText,
            fontWeight: 500,
            textTransform: "none"
          }}
        >
          {t["Cancel"]}
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isLoading}
          endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          onClick={onConfirm}
          sx={{
            minWidth: 140,
            height: 44,
            borderRadius: `${COLORS.radius.md}px`,
            fontWeight: 500,
            textTransform: "none",
            ...primaryContainedButtonSx
          }}
        >
          {t["Confirm"] || "Confirm"}
        </Button>
      </Box>
    </MyModal>
  );
}
